import { useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, Paginator, fmtDate, fmtINR, ExportButton } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUSES = ["all", "PENDING", "CONFIRMED", "PREPARING", "READY_FOR_PICKUP", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED", "REFUNDED"];
const NEXT = {
  PENDING: ["CONFIRMED", "CANCELLED"], CONFIRMED: ["PREPARING", "CANCELLED"], PREPARING: ["READY_FOR_PICKUP", "CANCELLED"],
  READY_FOR_PICKUP: ["OUT_FOR_DELIVERY", "CANCELLED"], PICKED_UP: ["OUT_FOR_DELIVERY", "CANCELLED"],
  ASSIGNED: ["CANCELLED"], OUT_FOR_DELIVERY: ["DELIVERED", "CANCELLED"], DELIVERED: ["REFUNDED"],
};

export default function Orders() {
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const limit = 20;
  const path = `/admin/orders?page=${page}&limit=${limit}${status !== "all" ? `&status=${status}` : ""}`;
  const { data, loading, error, refetch } = useFetch(path, [path]);
  const [detail, setDetail] = useState(null);
  const [refundOrder, setRefundOrder] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const openDetail = async (o) => {
    setDetail({ loading: true });
    try { const r = await api.get(`/orders/${o.id}`); setDetail(r.data); }
    catch (e) { toast.error(fmtErr(e)); setDetail(null); }
  };
  const changeStatus = async (o, newStatus) => {
    try { await api.post(`/orders/${o.id}/status`, { status: newStatus }); toast.success(`Order → ${newStatus.replace(/_/g, " ")}`); refetch(); if (detail?.id === o.id) openDetail(o); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const doRefund = async () => {
    setBusy(true);
    try { await api.post(`/admin/orders/${refundOrder.id}/refund`, { reason: reason || "Admin refund" }); toast.success("Order refunded"); setRefundOrder(null); setReason(""); refetch(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Orders" subtitle="Track and manage all marketplace orders"
        actions={<ExportButton filename="sonu-orders.csv" rows={data?.items} testid="export-orders-btn"
          columns={[{ key: "order_number", label: "Order #" }, { key: "status", label: "Status" }, { key: "payment_status", label: "Payment" }, { key: "payment_method", label: "Method" }, { key: "items", label: "Items", get: (r) => r.items?.length || 0 }, { key: "total", label: "Total", get: (r) => r.pricing?.total }, { key: "delivery_charge", label: "Delivery", get: (r) => r.pricing?.delivery_charge }, { key: "created_at", label: "Placed" }, { key: "id", label: "Order ID" }]} />} />
      <Tabs value={status} onValueChange={(v) => { setStatus(v); setPage(1); }} className="mb-4">
        <TabsList className="flex-wrap h-auto" data-testid="orders-status-tabs">
          {STATUSES.map((s) => <TabsTrigger key={s} value={s} data-testid={`orders-tab-${s}`}>{s === "all" ? "All" : s.replace(/_/g, " ")}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <SectionCard className="overflow-hidden">
        {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
          !data?.items?.length ? <EmptyState label="No orders" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr><th className="px-4 py-2.5 font-semibold">Order #</th><th className="px-4 py-2.5 font-semibold">Items</th><th className="px-4 py-2.5 font-semibold">Total</th><th className="px-4 py-2.5 font-semibold">Payment</th><th className="px-4 py-2.5 font-semibold">Status</th><th className="px-4 py-2.5 font-semibold">Placed</th><th className="px-4 py-2.5"></th></tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((o) => (
                    <tr key={o.id} className="hover:bg-muted/30" data-testid={`order-row-${o.id}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">{o.order_number}</td>
                      <td className="px-4 py-2.5 tnum">{o.items?.length || 0}</td>
                      <td className="px-4 py-2.5 tnum">{fmtINR(o.pricing?.total)}</td>
                      <td className="px-4 py-2.5"><StatusChip status={o.payment_status} /> <span className="text-[10px] text-muted-foreground ml-1">{o.payment_method}</span></td>
                      <td className="px-4 py-2.5"><StatusChip status={o.status} /></td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-2.5 text-right"><Button size="sm" variant="outline" onClick={() => openDetail(o)} data-testid={`order-view-${o.id}`}>View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </SectionCard>
      <Paginator page={page} total={data?.total} limit={limit} onPage={setPage} />

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="order-detail-drawer">
          <SheetHeader className="sr-only"><SheetTitle>Order details</SheetTitle></SheetHeader>
          {detail?.loading ? <Loading /> : detail && (
            <>
              <SheetHeader><SheetTitle className="font-mono text-base">{detail.order_number}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2"><StatusChip status={detail.status} /><StatusChip status={detail.payment_status} /><span className="text-xs text-muted-foreground">{detail.delivery_mode}</span></div>
                <div>
                  <h4 className="text-sm font-semibold mb-2">Items</h4>
                  <div className="space-y-1.5">
                    {detail.items?.map((it, i) => (
                      <div key={i} className="flex justify-between text-sm"><span>{it.name} × {it.quantity}</span><span className="tnum">{fmtINR(it.line_total)}</span></div>
                    ))}
                  </div>
                </div>
                <div className="border-t pt-3 space-y-1 text-sm">
                  {[["Subtotal", detail.pricing?.subtotal], ["Discount", -(detail.pricing?.product_discount || 0)], ["Coupon", -(detail.pricing?.coupon_discount || 0)], ["Tax (GST)", detail.pricing?.tax_total], ["Delivery", detail.pricing?.delivery_charge], ["Platform", detail.pricing?.platform_charge], ["Loyalty", -(detail.pricing?.loyalty_value_redeemed || 0)]].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-muted-foreground"><span>{k}</span><span className="tnum">{fmtINR(v)}</span></div>
                  ))}
                  <div className="flex justify-between font-bold pt-1 border-t"><span>Total</span><span className="tnum">{fmtINR(detail.pricing?.total)}</span></div>
                  {detail.pricing?.free_delivery_applied && <div className="text-xs text-emerald-600">✓ Free delivery applied (order #{detail.order_index})</div>}
                </div>
                {detail.status_history?.length > 0 && (
                  <div className="border-t pt-3">
                    <h4 className="text-sm font-semibold mb-2">Timeline</h4>
                    <div className="space-y-2">
                      {detail.status_history.map((h, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs"><StatusChip status={h.status} /><span className="text-muted-foreground">{fmtDate(h.created_at)}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border-t pt-3 space-y-2">
                  <h4 className="text-sm font-semibold">Update status</h4>
                  <div className="flex flex-wrap gap-2">
                    {(NEXT[detail.status] || []).map((ns) => (
                      <Button key={ns} size="sm" variant={ns === "CANCELLED" || ns === "REFUNDED" ? "destructive" : "default"} onClick={() => changeStatus(detail, ns)} data-testid={`order-status-${ns}`}>{ns.replace(/_/g, " ")}</Button>
                    ))}
                    {detail.status === "DELIVERED" && <Button size="sm" variant="destructive" onClick={() => { setRefundOrder(detail); }} data-testid="order-refund-btn">Refund</Button>}
                    {!(NEXT[detail.status] || []).length && detail.status !== "DELIVERED" && <span className="text-xs text-muted-foreground">No further transitions.</span>}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!refundOrder} onOpenChange={(o) => { if (!o) { setRefundOrder(null); setReason(""); } }}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader><DialogTitle>Refund {refundOrder?.order_number}</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Refund reason…" data-testid="refund-reason-input" />
          <DialogFooter><Button variant="outline" onClick={() => setRefundOrder(null)}>Cancel</Button><Button variant="destructive" onClick={doRefund} disabled={busy} data-testid="refund-confirm-btn">{busy ? "Processing…" : "Confirm refund"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
